import { constants as C } from '@trucktrax/trucktrax-common';
import {
  DriverDto,
  PositionDto,
  ProductLine,
  TicketDto,
  TruckDetailsDto,
  TruckDto,
  UrlKeyDto,
} from '@trucktrax/trucktrax-ts-common';

import { DefaultLocation } from '../components/geotrax/map/mapTypes';
import { MarkerType } from '../components/geotrax/map/TTMap';
import { DTO_URL_PREFIXES } from '../constants/appConstants';
import ARROW_MARKERS from '../constants/markerConstants';
import { MarkerInfo } from '../store/actions/mapviewActions';
import {
  HasNameParts,
  MapMarker,
  MapMarkerData,
  MapMarkerType,
  PositionDetailsDto,
} from '../types';
import { getFullName } from './adminUtil';
import { getIdFromUrl, isEmptyArray } from './appUtil';
import recordLog, { LogLevel } from './errorUtil';
import { isValidLatLng } from './geoUtil';
import { extractTrackedObjectFromPosition, formatTimeSince } from './positionsUtil';

const markersByStatus: { [key: string]: string[] } = {
  [C.STATUS_IDS.IN_QUEUE]: ARROW_MARKERS.IN_QUEUE,
  [C.STATUS_IDS.LOADING]: ARROW_MARKERS.LOADING,
  [C.STATUS_IDS.TICKETED]: ARROW_MARKERS.TICKETED,
  [C.STATUS_IDS.TO_JOB]: ARROW_MARKERS.TO_JOB,
  [C.STATUS_IDS.ON_JOB]: ARROW_MARKERS.ON_JOB,
  [C.STATUS_IDS.UNLOAD_START]: ARROW_MARKERS.UNLOAD_START,
  [C.STATUS_IDS.UNLOADED]: ARROW_MARKERS.UNLOADED,
  [C.STATUS_IDS.RETURNING]: ARROW_MARKERS.RETURNING,
  [C.STATUS_IDS.IN_YARD]: ARROW_MARKERS.IN_YARD,
  [C.STATUS_IDS.UNKNOWN]: ARROW_MARKERS.UNKNOWN,
};

export default function getArrow(bearing: number, status: string, stationary?: boolean): string {
  const dotIndex = 8;
  if (stationary) {
    return markersByStatus[status]?.[dotIndex] ?? ARROW_MARKERS.DEFAULT[dotIndex];
  }
  const index = Math.round(bearing / 45) % 8;
  return markersByStatus[status]?.[index] ?? ARROW_MARKERS.DEFAULT[index];
}

export function createTrackableMapMarkersFromPositions(
  positions: PositionDetailsDto[],
  selectedRegionUrl: string,
  drivers: DriverDto[] = [],
  allTrucks: TruckDto[] = [],
  includeTruckTraxGeoDrivers: boolean = false,
  bypassRegionFilter: boolean = false,
): MapMarker[] {
  let positionsInRegion = bypassRegionFilter
    ? positions
    : positions.filter(
        position =>
          selectedRegionUrl === position.region?.url ||
          selectedRegionUrl === position.ticketStatus?.region.url,
      );
  if (isEmptyArray(positionsInRegion)) {
    return [];
  }
  const excludeTruckTraxGeoDrivers = !includeTruckTraxGeoDrivers;
  if (excludeTruckTraxGeoDrivers) {
    positionsInRegion = positionsInRegion.filter(position => position.trackedObject);
  }
  const markers: MapMarker[] = [];
  positionsInRegion.forEach(position => {
    const marker = convertPositionToMapMarker(position, drivers, allTrucks);
    if (marker) {
      markers.push(marker);
    }
  });
  return markers;
}

export function convertPositionToMapMarker(
  position: PositionDetailsDto,
  drivers: DriverDto[],
  allTrucks: TruckDto[],
): MapMarker | null {
  const trackedObject = extractTrackedObjectFromPosition(position);
  if (!trackedObject) {
    recordLog(
      LogLevel.Warn,
      'Could not extract a tracked object in order convert position to map marker.',
      { position },
    );
    return null;
  }
  const markerType = determineMarkerTypeFromPosition(position);
  const driver = determineDriverMatchingPosition(position, drivers, allTrucks);
  // Use driver from position as fallback if not found in Redux driver list
  const driverDetails = driver || position.driver || null;
  const id = getIdFromUrl(trackedObject.url) || getIdFromUrl(driverDetails?.url);
  const name = trackedObject.truckAlias || getFullName(driverDetails as HasNameParts);
  const isActive = position.active;
  const lat = position.location.latitude;
  const lng = position.location.longitude;
  const { status, statusTimestamp } = position;
  const truckDetails = findTruck(trackedObject, position, allTrucks);

  if (!name || !isActive) {
    recordLog(
      LogLevel.Warn,
      'Can not convert a position to a map marker that does not have a name or is not active',
      { name, isActive },
    );
    return null;
  }

  let ticketUrl = '';
  let plantUrl = '';

  const isExternalMarker = isExternalMapMarker(trackedObject, position, driverDetails);
  if (position && position.ticket && position.ticket.url) {
    ticketUrl = position.ticket.url;
    plantUrl = position.ticket.plant?.url || '';
  } else {
    // For ALL non-ticketed trucks (Unknown, In Plant, Ready to Load), use driver's default plant
    // This enables plant filtering for trucks without active tickets (DEV-2749)
    plantUrl = truckDetails?.plant?.url || driverDetails?.primaryPlant?.url || '';
  }

  const text = status && statusTimestamp && formatTimeSince(statusTimestamp);
  const newMarker: MapMarker = {
    id,
    markerId: id,
    name,
    text,
    data: {
      markerId: id,
      lat: lat ?? 0,
      lng: lng ?? 0,
      isActive,
      status,
      isExternal: isExternalMarker,
      ticketUrl,
      plantUrl,
      truckUrl: position?.ticket?.truck?.url ?? '',
      driverUrl: driverDetails?.url,
      type: markerType,
      positionDetails: position,
      driverDetails,
      truckDetails,
      ticket: position.ticket,
    } as MapMarkerData,
    type: markerType,
  };
  return newMarker;
}

/**
 * we default to true because if there isn't a tracked object then
 * we know we are dealing with tracking a driver
 * which we only do in V3 and currently drivers in V3 are all external
 */
function isExternalMapMarker(
  trackedObject: TruckDetailsDto,
  position: PositionDetailsDto,
  driver?: DriverDto | null,
) {
  const isExternal = driver?.isExternal ?? position.driver?.isExternal ?? trackedObject?.isExternal;
  if (isExternal === undefined) {
    recordLog(
      LogLevel.Warn,
      '[positionUtil] Could not determine if map marker is or is not external. Defaulting map marker to external.',
      {
        driver,
        trackedObject,
      },
    );
    return true;
  }
  return isExternal;
}

/**
 * We use the existence of the trackedObject property
 * to differentiate between a Truck and a Driver as the marker type.
 */
export function determineMarkerTypeFromPosition(position: {
  trackedObject?: any;
  [key: string]: any;
}): MapMarkerType {
  if (position.trackedObject && position.trackedObject?.url?.includes(DTO_URL_PREFIXES.TRUCKS)) {
    return MapMarkerType.Truck;
  }
  return MapMarkerType.Driver;
}

export function determineMarkerId(position: any): string {
  const id =
    getIdFromUrl(position.trackedObject?.url) ||
    getIdFromUrl(position.driver?.url) ||
    getIdFromUrl(position.ticket?.driver?.url);
  return id;
}

export function createMapMarker(
  position: PositionMarkerDataInfo,
  drivers: DriverDto[] = [],
  trucks: TruckDto[] = [],
): MarkerType {
  const markerId = determineMarkerId(position);
  const markerType = determineMarkerTypeFromPosition(position);
  let truckData;
  if (trucks) {
    truckData = trucks.find(truck => (position.ticket as any)?.truck?.url === truck?.url);
    truckData ??= trucks.find(truck => position.trackedObject?.url === truck?.url);
  }

  const driver = determineDriverMatchingPosition(position, drivers, trucks);

  const marker: MarkerType = {
    markerId,
    type: markerType,
    markerData: {
      markerId,
      type: markerType,
      driverDetails: driver,
      truckDetails: { ...position, truckAlias: truckData?.truckAlias },
      ticket: position.ticket!,
    },
  };
  return marker;
}

function determineDriverMatchingPosition(
  position: PositionMarkerDataInfo,
  drivers: DriverDto[],
  trucks: TruckDto[],
): DriverDto | null {
  const trackedObjectUrl = position.trackedObject?.url;
  if (trackedObjectUrl && trackedObjectUrl.includes(DTO_URL_PREFIXES.DRIVERS)) {
    const driver = drivers.find(driver => driver.url === trackedObjectUrl);
    if (driver) {
      return driver;
    }
  }

  if (trackedObjectUrl && trackedObjectUrl.includes(DTO_URL_PREFIXES.TRUCKS)) {
    const truckDriverUrl = trucks.find(truck => truck.url === trackedObjectUrl)?.driver?.url;
    if (truckDriverUrl) {
      const driver = drivers.find(driver => driver.url === truckDriverUrl);
      if (driver) {
        return driver;
      }
    }
  }

  const positionDetails = position as PositionDetailsDto;
  const positionTicket = position.ticket as TicketDto;
  const potentialDriverUrl =
    positionDetails?.ticket?.driver?.url ||
    positionDetails?.driver?.url ||
    positionTicket?.driver?.url;
  if (potentialDriverUrl) {
    const driver = drivers.find(driver => driver.url === potentialDriverUrl);
    if (driver) {
      return driver;
    }
  }

  if (positionTicket?.truck?.url) {
    const truckDriverUrl = trucks.find(truck => truck.url === positionTicket?.truck?.url)?.driver
      ?.url;
    if (truckDriverUrl) {
      const driver = drivers.find(driver => driver.url === truckDriverUrl);
      if (driver) {
        return driver;
      }
    }
  }

  return null;
}

type MarkerGroups = { [type in MapMarkerType.Truck | MapMarkerType.Driver]: MapMarker[] };

export function groupMarkersByType(markers: MapMarker[]): MarkerGroups {
  const groups: MarkerGroups = { Truck: [], Driver: [] };

  markers?.forEach(marker => {
    if (marker.type === MapMarkerType.Driver) {
      groups[MapMarkerType.Driver].push(marker);
    } else if (marker.type === MapMarkerType.Truck) {
      groups[MapMarkerType.Truck].push(marker);
    }
  });

  return groups;
}

export function convertDriverMarkersToTruckMarkers(
  drivers: MapMarker[],
  trucks: TruckDto[],
): { convertedTruckMarkers: MapMarker[]; removedDrivers: string[] } {
  const convertedTruckMarkers: MapMarker[] = [];
  const removedDrivers: string[] = [];

  drivers?.forEach(driverMarker => {
    const truckUrl = driverMarker.data.truckUrl || driverMarker.data.truckDetails?.url;
    const currentTruckId = getIdFromUrl(truckUrl);
    const matchedTruck = trucks.find(truck => `${truck.id}` === currentTruckId);
    if (matchedTruck) {
      const truckMarker: MapMarker = {
        ...driverMarker,
        type: MapMarkerType.Truck,
        id: currentTruckId,
        markerId: currentTruckId,
        name: matchedTruck.truckAlias,
        data: {
          ...driverMarker.data,
          markerId: currentTruckId,
          truckAlias: matchedTruck.truckAlias,
          driverUrl: driverMarker.data.driverUrl,
          truckUrl: matchedTruck.url,
          type: MapMarkerType.Truck,
        },
      } as MapMarker;
      removedDrivers.push(driverMarker.id);
      convertedTruckMarkers.push(truckMarker);
    }
  });
  return { convertedTruckMarkers, removedDrivers };
}

export function filterActiveMarkersByRegion(markers: PositionMarkerDataInfo[], regionUrl: string) {
  const filteredMarkers = markers.filter((marker: PositionMarkerDataInfo) => {
    if (!marker || marker.active !== true) {
      return false;
    }
    const markerIsInCurrentRegion = marker.region && marker.region.url === regionUrl;
    if (marker.trackedObject) {
      return markerIsInCurrentRegion;
    }
    // we are dealing with a Driver as the tracked object
    return markerIsInCurrentRegion;
  });
  return filteredMarkers;
}

export function isMarkerType(marker: any): marker is MarkerType {
  return (
    !!marker &&
    !('data' in marker) &&
    ('markerData' in marker || ('positionDetails' in marker && 'driverDetails' in marker))
  );
}

export function isMapMarker(marker: any): marker is MapMarker {
  return !!marker && 'data' in marker;
}

export function isMarkerInfo(marker: any): marker is MarkerInfo {
  return !!marker && 'markerData' in marker && 'lat' in marker && 'lng' in marker;
}

function findTruck(
  trackedObject: TruckDetailsDto,
  position: PositionDetailsDto,
  allTrucks: TruckDto[],
) {
  const trackedObjectUrl = trackedObject?.url;
  const isTrackedObjectTruck = trackedObjectUrl?.includes(DTO_URL_PREFIXES.TRUCKS);
  const sourceTruckUrl = isTrackedObjectTruck ? trackedObjectUrl : position?.ticket?.truck?.url;
  if (!sourceTruckUrl) {
    recordLog(
      LogLevel.Warn,
      "Cannot determine the truck url from the trackedObject or the truck associated with the position's ticket",
    );
  }
  return allTrucks.find(truck => truck.url === sourceTruckUrl);
}

export function determineMarkerLocation(marker: unknown): DefaultLocation | undefined {
  if (!marker) {
    return undefined;
  }

  if (isMarkerInfo(marker)) {
    return { lat: marker.lat ?? 0, lng: marker.lng ?? 0 };
  }

  if (isMarkerType(marker)) {
    const lat =
      marker.positionDetails?.location?.lat ?? marker.positionDetails?.location?.latitude ?? 0;
    const lng =
      marker.positionDetails?.location?.lng ?? marker.positionDetails?.location?.longitude ?? 0;
    return { lat, lng };
  }

  if (isMapMarker(marker)) {
    return { lat: marker.data?.lat, lng: marker.data?.lng };
  }

  return undefined;
}

export function determineRouteHistoryMarkerLocation(
  marker: MarkerInfo,
): DefaultLocation | undefined {
  if (!marker) {
    return undefined;
  }
  const truckLocation = marker.markerData?.truckDetails?.location;
  if (!truckLocation || !isValidLatLng(truckLocation.latitude, truckLocation.longitude)) {
    return undefined;
  }

  return { lat: truckLocation.latitude, lng: truckLocation.longitude };
}

export function determineMarkerHasValidLatLng(marker: unknown): boolean {
  if (!marker) {
    return false;
  }

  if (isMapMarker(marker)) {
    return isValidLatLng(marker.data?.lat, marker.data?.lng);
  }

  if (isMarkerType(marker)) {
    const lat =
      marker.positionDetails?.location?.lat ?? marker.positionDetails?.location?.latitude ?? 0;
    const lng =
      marker.positionDetails?.location?.lng ?? marker.positionDetails?.location?.longitude ?? 0;
    return isValidLatLng(lat!, lng!);
  }

  if (isMarkerInfo(marker)) {
    return isValidLatLng(marker.lat, marker.lng);
  }
  return false;
}

export function determineMarkerProductLine(marker: unknown): ProductLine | undefined {
  if (isMapMarker(marker)) {
    return marker.data?.positionDetails?.productLine;
  }

  if (isMarkerType(marker)) {
    return marker.positionDetails?.productLine;
  }

  return undefined;
}

export function determineMapMarkerId(marker: unknown): string | undefined {
  if (isMapMarker(marker)) {
    return marker.markerId ?? marker.id;
  }

  if (isMarkerType(marker)) {
    return marker.markerId;
  }
  if (isMarkerInfo(marker)) {
    return marker.markerId ?? marker.id;
  }

  return undefined;
}

export function determineMarkerPositionId(marker: unknown): string | undefined {
  const position = determineMarkerPosition(marker);
  return position?.id;
}

export function determineMarkerPosition(marker: unknown): PositionDetailsDto | undefined {
  if (isMapMarker(marker)) {
    return marker.data?.positionDetails;
  }

  if (isMarkerType(marker)) {
    return marker.positionDetails;
  }

  if (isMarkerInfo(marker)) {
    return marker.markerData?.positionDetails;
  }

  return undefined;
}

export interface PositionMarkerDataInfo extends Omit<PositionDto, 'ticket'> {
  ticket?: UrlKeyDto | null;
  trackedObjectName?: string;
  truckAlias?: string;
}
